import React, { useMemo } from "react";
import { Marker, Popup } from "react-leaflet";
import { Icon } from "leaflet";
import type { BikeMarker as BikeMarkerType } from "../../types/map";
import {
  formatTimeAgo,
  formatSpeed,
  formatBattery,
  getStatusBadgeColor,
} from "../../utils/formatters";
import { BIKE_MARKER_COLORS } from "../../utils/constants";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";

interface BikeMarkerProps {
  bike: BikeMarkerType;
  markerImageUrl?: string | null;
  makerColor?: string | null;
  onClick?: (bikeId: string) => void;
}

export const BikeMarker: React.FC<BikeMarkerProps> = ({
  bike,
  markerImageUrl,
  makerColor,
  onClick,
}) => {
  const { categories } = useVehicleCategoryStore();

  const bikeCategory = useMemo(() => {
    const cat = categories.find(
      (category) => category.category_id === bike.category_id,
    );
    return cat?.category_name;
  }, [categories, bike.category_id]);

  const getMarkerColor = () => {
    if (bike.status === "maintenance") return BIKE_MARKER_COLORS.MAINTENANCE;
    if (bike.status === "inactive") return BIKE_MARKER_COLORS.INACTIVE;
    return bike.is_moving
      ? BIKE_MARKER_COLORS.ACTIVE_MOVING
      : BIKE_MARKER_COLORS.ACTIVE_STOPPED;
  };

  const createBikeIcon = () => {
    if (markerImageUrl) {
      return new Icon({
        iconUrl: markerImageUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -16],
      });
    }

    const color =
      makerColor && /^#[0-9A-Fa-f]{6}$/.test(makerColor)
        ? makerColor
        : bikeCategory
          ? getMarkerColor()
          : "#FF0000";

    return new Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
          <path d="M39.7887 37.6219L26 51.4104L12.2114 37.6219C4.59619 30.0065 4.59619 17.6599 12.2114 10.0447C19.8266 2.42945 32.1733 2.42945 39.7887 10.0447C47.4039 17.6599 47.4039 30.0065 39.7887 37.6219ZM26 28.1666C28.3933 28.1666 30.3333 26.2266 30.3333 23.8333C30.3333 21.44 28.3933 19.4999 26 19.4999C23.6067 19.4999 21.6667 21.44 21.6667 23.8333C21.6667 26.2266 23.6067 28.1666 26 28.1666Z" fill="${color}"/>
        </svg>
      `)}`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -16],
    });
  };

  if (!bike.latitude || !bike.longitude) {
    return null;
  }

  return (
    <Marker
      position={[bike.latitude, bike.longitude]}
      icon={createBikeIcon()}
      eventHandlers={{
        click: () => onClick?.(bike.bike_id),
      }}
    >
      <Popup>
        <div className="p-2 min-w-64">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{bike.bike_id}</h3>
            <span
              className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(
                bike.status,
              )}`}
            >
              {bike.status}
            </span>
          </div>

          <div className="space-y-1 text-sm text-gray-600">
            <div>
              <span className="font-medium">Category:</span>{" "}
              {bikeCategory || "-"}
            </div>
            <div>
              <span className="font-medium">Location:</span>{" "}
              {bike.latitude.toFixed(6)}, {bike.longitude.toFixed(6)}
            </div>
            <div>
              <span className="font-medium">Speed:</span>{" "}
              {formatSpeed(bike.speed)}
            </div>
            <div>
              <span className="font-medium">Battery:</span>{" "}
              {formatBattery(bike.battery_level)}
            </div>
            <div>
              <span className="font-medium">Last seen:</span>{" "}
              <span title={new Date(bike.last_seen).toLocaleString()}>
                {formatTimeAgo(bike.last_seen)}
              </span>
            </div>
            <div>
              <span className="font-medium">Status:</span>{" "}
              {bike.is_moving ? "Moving" : "Stationary"}
            </div>
          </div>

          <div className="mt-3 pt-2 border-t border-gray-200">
            <button
              onClick={() => onClick?.(bike.bike_id)}
              className="w-full bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};
